import { OrganizationType } from "../types/organization.type";
import { orgScopedPermission } from "./jwt";
import type { LegacyRole } from "./types";

export const APPETITE_WRITE_ROLES: readonly LegacyRole[] = [
  "admin",
  "recycler_admin",
  "recycler_ops_manager",
  "recycler_staff",
];

export const APPETITE_READ_ROLES: readonly LegacyRole[] = [
  ...APPETITE_WRITE_ROLES,
];

export const PRE_ORDER_WRITE_ROLES: readonly LegacyRole[] = [
  "admin",
  "recycler_admin",
  "recycler_ops_manager",
  "recycler_staff",
];

export const PRE_ORDER_READ_ROLES: readonly LegacyRole[] = [
  ...PRE_ORDER_WRITE_ROLES,
];

export const MATERIAL_TYPE_WRITE_ROLES: readonly LegacyRole[] = [
  "admin",
  "recycler_admin",
  "recycler_ops_manager",
];

export const MATERIAL_TYPE_READ_ROLES: readonly LegacyRole[] = [
  ...MATERIAL_TYPE_WRITE_ROLES,
  "recycler_staff",
];

/** The waste-type basket lives on the organization, so its endpoints are guarded
 *  by organization.read / organization.write rather than material_type.*. */
export const ORGANIZATION_WRITE_ROLES: readonly LegacyRole[] = MATERIAL_TYPE_WRITE_ROLES;
export const ORGANIZATION_READ_ROLES: readonly LegacyRole[] = MATERIAL_TYPE_READ_ROLES;

export interface MarketNavItem {
  text: string;
  path: string;
  allowedTypes: OrganizationType[];
  entity: string;
  action: string;
  legacyRoles?: readonly LegacyRole[];
}

const RECYCLER_ONLY = [OrganizationType.RECYCLER];

/** Market top-nav catalog — permission + org-type gating. */
export const MARKET_NAV_CATALOG: MarketNavItem[] = [
  {
    text: "Supply",
    path: "/supply",
    allowedTypes: RECYCLER_ONLY,
    entity: "appetite",
    action: "write",
    legacyRoles: APPETITE_WRITE_ROLES,
  },
  {
    text: "Dashboard",
    path: "/dashboard",
    allowedTypes: RECYCLER_ONLY,
    entity: "appetite",
    action: "read",
    legacyRoles: APPETITE_READ_ROLES,
  },
  {
    text: "Orders",
    path: "/orders",
    allowedTypes: RECYCLER_ONLY,
    entity: "pre_order",
    action: "list",
    legacyRoles: PRE_ORDER_READ_ROLES,
  },
  {
    text: "Materials",
    path: "/materials",
    allowedTypes: RECYCLER_ONLY,
    entity: "organization",
    action: "read",
    legacyRoles: ORGANIZATION_READ_ROLES,
  },
];

export function resolveMarketNavPermission(
  entity: string,
  action: string,
  orgId: string | null,
): string | null {
  if (!orgId) {
    return null;
  }
  return orgScopedPermission(orgId, entity, action);
}

export const PERM_APPETITE_WRITE = (orgId: string) =>
  orgScopedPermission(orgId, "appetite", "write");
export const PERM_APPETITE_READ = (orgId: string) =>
  orgScopedPermission(orgId, "appetite", "read");
export const PERM_DASHBOARD_READ = (orgId: string) =>
  orgScopedPermission(orgId, "dashboard", "read");
export const PERM_OFFER_LIST = (orgId: string) =>
  orgScopedPermission(orgId, "offer", "list");
export const PERM_PRE_ORDER_LIST = (orgId: string) =>
  orgScopedPermission(orgId, "pre_order", "list");
export const PERM_PRE_ORDER_CREATE = (orgId: string) =>
  orgScopedPermission(orgId, "pre_order", "create");
export const PERM_PRE_ORDER_WRITE = (orgId: string) =>
  orgScopedPermission(orgId, "pre_order", "write");
export const PERM_PRE_ORDER_DELETE = (orgId: string) =>
  orgScopedPermission(orgId, "pre_order", "delete");
export const PERM_ORGANIZATION_READ = (orgId: string) =>
  orgScopedPermission(orgId, "organization", "read");
export const PERM_ORGANIZATION_WRITE = (orgId: string) =>
  orgScopedPermission(orgId, "organization", "write");
export const PERM_MATERIAL_TYPE_LIST = (orgId: string) =>
  orgScopedPermission(orgId, "material_type", "list");
export const PERM_MATERIAL_TYPE_CREATE = (orgId: string) =>
  orgScopedPermission(orgId, "material_type", "create");
export const PERM_MATERIAL_TYPE_WRITE = (orgId: string) =>
  orgScopedPermission(orgId, "material_type", "write");
export const PERM_MATERIAL_TYPE_DELETE = (orgId: string) =>
  orgScopedPermission(orgId, "material_type", "delete");
